/* eslint-disable no-unused-vars,object-curly-newline */
/* global React */
import { useMemoRef, useStyles } from '@platform/react/hook'
import { Box, Grid } from '@mui/material'
import FileImage from 'ui/Element/Attachment/Item/File'
import Icon from 'ui/Element/Icon'
import { useCallback, useMemo, useState } from 'react'
import usePreviewableAttachment from 'platform/react/hook/usePreviewableAttachment'
import { isRemote } from 'platform/adapter/attachment/helper'
import CircleButton from 'ui/Element/Attachment/List/Template/CircleButton'
import ProgressCircle from 'ui/Element/Progress/Circle'

/**
 * Returns the AttachmentListTemplateSmall element's current theme styles.
 *
 * @param {Object} theme        the application's current theme
 * @param {number} spacing      padding for each image
 * @param {number} space
 * @param {number} gap
 * @param {number|string} size  size of the image
 * @param {boolean} rigid       whether to omit any padding
 * @returns {Object}        the AttachmentListTemplateSmall component's styles
 */
const styles = (theme, { spacing, space, gap, size, rigid }) => ({
  root: {
    position: 'relative',
    alignItems: 'stretch',
    ...rigid
      ? {
        width: size,
        height: size,
        minHeight: size,
        padding: 0,
        flexShrink: 0,
      }
      : {
        minHeight: '5rem',
        padding: spacing
          ? theme.spacing(spacing)
          : theme.spacing(space, gap),
      },
  },
  image: {
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left',
    backgroundSize: 'cover',
  },
  progress: {
    top: '-100%',
    width: '100%',
    height: '100%',
    opacity: 0.4,
  },
  fab: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
  },
  delete: {
    position: 'absolute',
    top: 'calc(-1.25rem / 2)', // same as height
    right: 'calc(-1.25rem / 2)', // same as width
    zIndex: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.25rem',
    height: '1.25rem',
    borderRadius: '50%',
    backgroundColor: theme.palette.black.main,
  },
  ...theme.custom.attachmentListTemplateSmall,
})

/**
 * Attachment List Template Small
 *
 * Displays a small representation of the file to be displayed in a grid.
 *
 * @param {Object} attachment                 the attached file
 * @param {Object} types                      the types for the {@link FileImage} element
 * @param {Object} [classes]                  the element's style classes
 * @param {string} [group]                    the attachment group identifier
 * @param {Object} [props]                    additional element's properties
 * @param {boolean} [props.showProgress=true] whether to show a progress bar
 * @param {number} [props.size=4]             the number of grids used by the element
 */
const AttachmentListTemplateSmall = ({
  attachment,
  types,
  classes: parentClasses,
  group,
  ...props
}) => {
  const {
    spacing = 1,
    space = 1,
    gap = 1,
    size = 4,
    rigid = false,
    bordered,
    fit,
    shape,

    events,

    download = true,
    showDownload = true,
    showProgress = true,
    showDelete = true,
    skipSkeleton = false,
    skipPlaceholder = false,
  } = props

  const classes = { ...useStyles(styles, { spacing, space, gap, size, rigid })(), ...parentClasses }
  const { value: item } = attachment
  const [attachmentState, setAttachmentState] = useState({})
  const { src = null } = attachmentState || {}
  const { file, upload } = attachment
  const isPreviewable = usePreviewableAttachment({ attachment })
  const showDownloadButton = !upload && showDownload && !isPreviewable && !src && !file

  const handleDelete = useCallback(events?.onDelete?.(group, attachment), [attachment])
  const canDelete = useMemo(
    () => events?.onDelete && (attachment?.acl?.includes?.(40) || !isRemote(attachment) || false),
    [attachment],
  )

  return (
    <Grid
      item
      className={`${classes.root} ${classes?.image ? classes.image : ''}`}
      title={decodeURIComponent(item.name)}
    >
      {showDelete && canDelete && (
        <Box
          className={classes.delete}
          onClick={handleDelete}
        >
          <Icon
            name={'close'}
            size={'0.75rem'}
            color={'white.main'}
          />
        </Box>
      )}
      <FileImage
        types={types}
        group={group}
        file={file}
        download={download}
        attachment={attachment}

        fit={fit}
        size={size}
        shape={shape}
        rounded={true}
        bordered={bordered}

        skipSkeleton={skipSkeleton}
        skipPlaceholder={skipPlaceholder}
        events={{
          onClick: events?.onClick,
          onState: setAttachmentState,
        }}
        placeholderProps={{
          size: '50%',
          color: 'gray.800',
          background: 'gray.900',
        }}
        xs={12}
        sm={12}
        md={12}
        lg={12}
        xl={12}
      />
      {upload && showProgress && (
        <Box className={classes.fab}>
          <ProgressCircle
            value={upload.progress}
            color={'black.main'}
            indeterminate={upload.progress <= 0 || upload.progress >= 100}
          />
        </Box>
      )}
      {showDownloadButton && (
        <Box className={classes.fab}>
          <CircleButton
            group={group}
            duration={50}
            download={false}
            attachment={attachment}
            showProgress={showProgress}
            background={'white.main'}
            color={'black.main'}
            icon={'download'}
            events={{ onClick: events?.onClick }}
          />
        </Box>
      )}
    </Grid>
  )
}
export default useMemoRef(AttachmentListTemplateSmall, props => [
  props.classes,
  props.attachment,
])
